import { isBuilderPage } from '../render/ElementPlaceholder';
import { textProp } from '../render/elementProps';
import { useElementBox, type ElementComponent } from '../render/elementRenderContext';
import { fieldIdOf, useEnclosingForm } from './formScope';
import './forms.css';

/**
 * The caption above a field, for when the field's own label is not where the merchant wants it.
 *
 * A `label` names a field by that field's name, never by a DOM id — a merchant types `phone`, not
 * `xs-node-14-field` — so the form in scope is asked which of its fields answers to that name and the
 * `for` is written from the answer. A `for` pointing at nothing is worse than no label at all: a screen
 * reader announces the field as unnamed while the sighted page looks perfectly captioned. So a name
 * that resolves to no field is drawn as plain words, and the builder is told why.
 */

/** What the builder is told when a caption names a field that is not in the form around it. */
const NO_TARGET = 'This label names no field in the form around it.';

// Draws a caption: a real label when it can reach its field, and honest plain words when it cannot.
export const LabelElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const words = textProp(node.props, 'text');
  const names = textProp(node.props, 'htmlFor');
  const form = useEnclosingForm();
  const target = names === '' ? null : fieldIdOf(form?.rules ?? [], names);
  if (target) return <label {...box} htmlFor={target} dir="auto">{words}</label>;
  return (
    <span {...box} dir="auto">
      {words}
      {names !== '' && isBuilderPage() ? <em className="xs-form-hint">{NO_TARGET}</em> : null}
    </span>
  );
};
